import {
  Badge,
  Checkbox,
  Group,
  NumberFormatter,
  Select,
  Text,
  Tooltip,
} from "@mantine/core";
import ModelCardTooltip from "./ModelCardTooltip";
import {
  isDeprecated,
  isUnavailable,
  lifecycleMeta,
  lifecycleOf,
} from "./modelLifecycle";
import { IconCurrencyDollar, IconRoute } from "@tabler/icons-react";
import { useState } from "react";
import { useStore } from "../store";

// Hover state is kept per-option so moving the cursor between options doesn't
// re-render the parent Select — which would wipe Mantine's internal searchValue.
// Tooltip cleanup on click is handled by `keepMounted: false` unmounting options.
function ModelOptionRow({ option }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Tooltip
      opened={hovered}
      label={<ModelCardTooltip model={option.model} />}
      withArrow
    >
      <Group
        flex="1"
        gap="xs"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Group flex="1" justify="space-between">
          <Group gap="xs" wrap="nowrap">
            {option.label}
            {lifecycleOf(option.model) !== "active" && (
              <Badge
                size="xs"
                variant="light"
                color={lifecycleMeta(option.model).color}
              >
                {lifecycleMeta(option.model).label}
              </Badge>
            )}
          </Group>
          <Group>
            <Badge
              leftSection={<IconCurrencyDollar size={12} />}
              radius="xs"
              color="gray"
              size="xs"
            >
              <NumberFormatter
                value={option.model.pricingGraduations[0].input}
                decimalScale={2}
                fixedDecimalScale
              />
              {" / "}
              <NumberFormatter
                value={option.model.pricingGraduations[0].output}
                decimalScale={2}
                fixedDecimalScale
              />
            </Badge>
          </Group>
        </Group>
      </Group>
    </Tooltip>
  );
}

function RoutingAliasOptionRow({ option }) {
  const [hovered, setHovered] = useState(false);
  return (
    <Tooltip
      opened={hovered}
      label={
        <div>
          <Text size="xs" fw={500}>
            Routing Alias: {option.rule.strategy}
          </Text>
          <Text size="xs" c="dimmed" mt="xs">
            Routes to: {option.rule.models?.join(", ")}
          </Text>
          {option.rule.description && (
            <Text size="xs" c="dimmed" mt="xs">
              {option.rule.description}
            </Text>
          )}
        </div>
      }
      withArrow
    >
      <Group
        flex="1"
        gap="xs"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Group flex="1" justify="space-between">
          <Group gap="xs">
            <IconRoute size={14} />
            {option.label}
          </Group>
          <Badge radius="xs" color="blue" size="xs">
            {option.rule.strategy}
          </Badge>
        </Group>
      </Group>
    </Tooltip>
  );
}

export default function ModelSelect({
  value,
  onChange,
  data,
  routingRules,
  label = "Select model",
  ...props
}) {
  const [showDeprecated, setShowDeprecated] = useState(false);
  const providers = useStore((state) => state.providers);

  const supportedModels =
    providers?.flatMap((provider) => provider.models) ?? null;

  // Check if the current value is a routing alias
  const isRoutingAlias = routingRules?.some((rule) => rule.alias === value);
  const routingRule = routingRules?.find((rule) => rule.alias === value);

  // Always keep the currently-selected model visible so the user can see
  // what's set. Otherwise: "unavailable" (retired) models can't be called, so
  // they're never offered; deprecated models are still usable but hidden
  // behind a toggle.
  const filteredModels =
    supportedModels?.filter((model) => {
      if (model.name === value) return true;
      if (model.isDecision) return false;
      if (isUnavailable(model)) return false;
      if (isDeprecated(model)) return showDeprecated;
      return true;
    }) ?? null;

  data ??= filteredModels?.map((model) => ({
    value: model.name,
    label: model.name,
    model: model,
  }));

  // Add routing aliases to the data
  if (routingRules && routingRules.length > 0) {
    const routingData = routingRules.map((rule) => ({
      value: rule.alias,
      label: rule.alias,
      isRoutingAlias: true,
      rule: rule,
    }));
    data = [...routingData, ...data];
  }

  // A model name can be offered by more than one provider (e.g. Gemini and
  // GeminiVertex share the same model descriptors), and a routing alias could
  // collide with a model name. Mantine's Select throws on duplicate option
  // values, so collapse to the first occurrence of each value.
  if (data) {
    const seen = new Set();
    data = data.filter((option) => {
      if (seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });
  }

  const renderSelectOption = ({ option }) =>
    option.isRoutingAlias ? (
      <RoutingAliasOptionRow option={option} />
    ) : (
      <ModelOptionRow option={option} />
    );

  return (
    <Select
      label={
        <Group justify="space-between" align="flex-end">
          {label}
          <Checkbox
            size="xs"
            checked={showDeprecated}
            onChange={(event) => setShowDeprecated(event.currentTarget.checked)}
            label="Show deprecated"
          />
        </Group>
      }
      placeholder="Select a model"
      value={value}
      data={data}
      onChange={onChange}
      comboboxProps={{ keepMounted: false }}
      searchable
      renderOption={renderSelectOption}
      leftSection={isRoutingAlias ? <IconRoute size={16} /> : null}
      rightSection={
        isRoutingAlias ? (
          <Tooltip
            label={
              <div>
                <Text size="xs">This is a routing alias that resolves to:</Text>
                <Text size="xs" fw={500} mt="xs">
                  {routingRule?.models?.join(", ")}
                </Text>
                <Text size="xs" c="dimmed" mt="xs">
                  Strategy: {routingRule?.strategy}
                </Text>
              </div>
            }
          >
            <Badge size="xs" color="blue" variant="light">
              Alias
            </Badge>
          </Tooltip>
        ) : null
      }
      {...props}
    />
  );
}
